import { Pesel } from '@dreryk/personal-external-identifier-common-api';
import { Gender, Guardian } from '@dreryk/egabinet-patient-frontend-api';

import { GuardianExternalIdentifierType } from '../PersonForm/GuardianExternalIdentifier/GuardianExternalIdentifierTypeField';

import { GuardianModalFormBean, StatutoryRepresentative } from './GuardianModal.types';
import { GuardianDetailsFormBean } from './GuardianDetailsModal/GuardianDetailsModal';

export const getGuardianGender = (
   identifierType: GuardianExternalIdentifierType,
   identifierValue: string | Pesel | undefined | null,
   existingGender?: Gender
): Gender | undefined => {
   if (existingGender) {
      return existingGender;
   }
   if (identifierType !== 'Pesel' || !identifierValue) {
      return undefined;
   }
   if (identifierValue instanceof Pesel) {
      return identifierValue.getGender();
   }
   return Pesel.isValid(identifierValue) ? Pesel.of(identifierValue).getGender() : undefined;
};

export const getInitialValues = (
   initialValues: Guardian | GuardianModalFormBean | undefined,
   statutoryRepresentative?: boolean
): Partial<GuardianModalFormBean> => {
   if (initialValues) {
      if ('id' in initialValues) {
         return {
            relationCode: initialValues.relation,
            statutoryRepresentative: initialValues.statutoryRepresentative
               ? StatutoryRepresentative.YES
               : StatutoryRepresentative.NO,
            guardian: {
               firstName: initialValues.firstName.asString(),
               middleName: initialValues.middleName?.asString() ?? null,
               lastName: initialValues.lastName.asString(),
               gender: null,
               ...initialValues.externalIdentifier.accept<
                  Pick<
                     GuardianDetailsFormBean,
                     | 'guardianExternalIdentifierType'
                     | 'guardianExternalIdentifierValue'
                     | 'passportCountry'
                     | 'passportValidTo'
                     | 'nationalIdentificationNumberCountry'
                  >
               >({
                  visitPesel: (pesel) => ({
                     guardianExternalIdentifierType: 'Pesel',
                     guardianExternalIdentifierValue: pesel.asString(),
                     passportCountry: null,
                     passportValidTo: null,
                     nationalIdentificationNumberCountry: null,
                  }),
                  visitPassport: (passport) => ({
                     guardianExternalIdentifierType: 'Passport',
                     guardianExternalIdentifierValue: passport.getValue(),
                     passportCountry: passport.getCountry(),
                     passportValidTo: passport.getValidTo(),
                     nationalIdentificationNumberCountry: null,
                  }),
                  visitNationalIdentificationNumber: (nationalIdentificationNumber) => ({
                     guardianExternalIdentifierType: 'NationalIdentificationNumber',
                     guardianExternalIdentifierValue: nationalIdentificationNumber.getValue(),
                     nationalIdentificationNumberCountry: nationalIdentificationNumber.getCountry(),
                     passportCountry: null,
                     passportValidTo: null,
                  }),
               }),
               country: initialValues.address.getCountry(),
               flat: initialValues.address.findFlat()?.asString() ?? null,
               house: initialValues.address.findBuilding()?.asString() ?? null,
               postcode: initialValues.address.findPostcode() ?? null,
               street: initialValues.address.findStreet()?.asString() ?? null,
               town: initialValues.address.findTown()?.getName() ?? null,
               emailAddress: initialValues.emailAddress ?? null,
               phoneNumber: initialValues.phoneNumber ?? null,
            },
            fullName: `${initialValues.lastName} ${initialValues.firstName}`,
         };
      }
      return {
         guardian: initialValues.guardian!,
         statutoryRepresentative: initialValues.statutoryRepresentative,
         relationCode: initialValues.relationCode,
         fullName: `${initialValues.guardian!.firstName} ${initialValues.guardian!.lastName}`,
      };
   }
   return {
      statutoryRepresentative: statutoryRepresentative ? StatutoryRepresentative.YES : StatutoryRepresentative.NO,
   };
};
